import { useRef, useState } from "react";
import { catalogApi, type CatalogSettings } from "@/lib/api/catalog";
import { validateCatalogForm } from "@/lib/catalog/catalog-validate-form";
import {
  executeCatalogWorkflowAction,
  type CatalogWorkflowAction,
  type PublicationStatus,
} from "@/lib/catalog/catalog-workflow";
import { buildJournalPayload, type JournalCatalogState } from "./journal-catalog-form";
import { catalogErrorMessage } from "./journal-catalog-error";

interface WorkflowToast {
  success: (message: string) => void;
  error: (message: string) => void;
}

interface JournalWorkflowOptions {
  id?: string;
  state: JournalCatalogState;
  settings: CatalogSettings;
  publicationStatus?: PublicationStatus;
  navigate: (path: string) => void;
  toast: WorkflowToast;
  onSaved: () => void;
}

export function useJournalCatalogWorkflow({
  id,
  state,
  settings,
  publicationStatus,
  navigate,
  toast,
  onSaved,
}: JournalWorkflowOptions) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const submitLock = useRef(false);

  const run = async (action: CatalogWorkflowAction) => {
    if (submitLock.current) return;
    const payload = buildJournalPayload(state);
    const nextErrors = validateCatalogForm(
      {
        biblio: payload.biblio,
        journal: payload.journal,
        journalHoldings: payload.journalHoldings,
        copies: payload.copies,
        itemDefaults: payload.itemDefaults,
        itemCopies: payload.itemCopies,
      },
      {
        method: "JOURNAL",
        mode: action === "draft" ? "draft" : "publish",
        accessionNumberMode: settings.accessionNumberMode,
      }
    );
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Please fix the errors before saving.");
      return;
    }
    submitLock.current = true;
    setSubmitting(true);
    try {
      if (action === "draft" && id) {
        await catalogApi.updateJournalDraft(id, payload);
        onSaved();
        toast.success(`Journal "${payload.biblio.title}" draft updated.`);
        return;
      }
      const result = await executeCatalogWorkflowAction({
        action,
        createPayload: payload,
        biblioId: id,
        biblioUpdate: id ? payload.biblio : undefined,
        publicationStatus,
      });
      onSaved();
      if (action === "draft") {
        toast.success(`Journal "${payload.biblio.title}" saved as draft.`);
        navigate(`/catalog/journal/draft/${result.biblioId}`);
      } else if (action === "submit-for-review") {
        toast.success(`Journal "${payload.biblio.title}" submitted for review.`);
        navigate("/staff/catalog/drafts");
      } else {
        toast.success(`Journal "${payload.biblio.title}" cataloged successfully!`);
        navigate(`/catalog/journal/${result.biblioId}`);
      }
    } catch (error: unknown) {
      toast.error(catalogErrorMessage(error, "Failed to catalog journal."));
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };
  return { errors, submitting, run };
}
